import * as d3 from 'd3';
import { LitElement, unsafeCSS } from '~/lib/bn-lit-element';
import { colors } from '~/utility/BinSentry-ui-utility';
import { css, svg } from '../../utility/lit-entity';
import { DeviceMonitoringChartLayerMixin } from './device-monitoring-chart-layer-mixin';
import '../../common/bn-icon';
import './device-events';

export const colour = css`var(--fill-level-high)`;
export const hasWarning = (event) => !!event.lastRunError;
export const hasAlert = (event) => !!event.sensorCriticalError;

class DeviceMonitoringChartLayerLevels extends DeviceMonitoringChartLayerMixin(LitElement) {
  static get styles() {
    return [...super.styles, css`
      .item .highlight {
        stroke: rgba(0, 0, 0, 0);
        fill: rgba(0, 0, 0, 0);
        opacity: 0;
      }
      .item .visual {
        opacity: .6;
      }
      .item.hovered .visual {
        opacity: 1;
      }
      .item.floating-event .visual {
        fill: var(--gray-60);
        stroke: var(--gray-80);
        stroke-width: 2px;
        stroke-dasharray: 4px 2px;
      }
      .item.floating-event line {
        stroke-dasharray: 4px 2px;
        stroke-width: 2px;
        stroke: var(--gray-80);
      }
      .no-data-banner {
        margin: 4em 4em 0 4em;
        text-align: center;
        padding: 1em;
      }
      .item {
        stroke-width: 1px;
      }
      .item .highlight {
        fill: transparent;
        stroke: transparent;
      }
      .item.hovered {
        stroke-width: 2px;
      }
      .item .visual {
        fill: ${colour};
        stroke-width: 0;
      }
      .item.sensor-error .visual {
        fill: var(--red-20);
      }
      .item.sensor-error .highlight {
        opacity: 1;
        fill: ${unsafeCSS(`${colors.red[20]}50`)};
        stroke: var(--red-20);
      }
      .item.sensor-error.hovered .highlight {
        fill: var(--red-20);
      }
      .item.sensor-error.hovered .visual {
        fill: var(--red-20);
      }
      .item.last-run-error .visual {
        fill: var(--orange-20);
      }
      .item.last-run-error .highlight {
        opacity: 1;
        fill: ${unsafeCSS(`${colors.orange[20]}50`)};
        stroke: var(--orange-20);
      }
      .item.last-run-error.hovered .highlight {
        fill: var(--orange-20);
      }
      .item.last-run-error.hovered .visual {
        fill: var(--orange-30);
      }
      .item.selected .highlight,
      .item.last-run-error.selected .highlight,
      .item.sensor-error.selected .highlight {
        fill: var(--blue-30);
        stroke-width: 0;
      }
      .item.selected .visual,
      .item.last-run-error.selected .visual,
      .item.sensor-error.selected .visual {
        fill: var(--blue-50);
      }
    `];
  }

  renderLayerSVG() {
    this._parseData();

    if (!this.levels || this.levels.length === 0) {
      return svg``;
    }

    this.yScale = d3.scaleLinear()
      .range([1, this.barHeight])
      .domain([0, this.maxTotalVolume]);

    return svg`
      <g>${this._renderFloatingEvents(this.floatingEvents)}</g>
      <g>${this._renderLevels(this.levelsEntities)}</g>
    `;
  }

  _renderLevels(levels) {
    return levels.map((level) => {
      const scaledDate = this.xScale(level.date);
      const highlightX = scaledDate - (this.levelWidthPadded / 2);
      const visualX = scaledDate - (this.levelWidth / 2);
      const visualHeight = this.yScale(level.value === undefined ? this.maxTotalVolume : level.value);
      const visualY = this.barHeightPadded - visualHeight - this.padding;
      const classes = ['item', 'level', ...this.getSelectableClassList(level)];
      if (level.sensorCriticalError) {
        classes.push('sensor-error');
      }

      if (level.lastRunError) {
        classes.push('last-run-error');
      }

      return svg`
        <g class="${classes.join(' ')}">
          <rect
            x="${highlightX}"
            y="${0}"
            height="${this.barHeightPadded}"
            width="${this.levelWidthPadded}"
            class="highlight"
          ></rect>
          <rect
            x="${visualX}"
            y="${visualY}"
            height="${visualHeight}"
            width="${this.levelWidth}"
            class="visual"
          ></rect>
        </g>
      `;
    });
  }

  _renderFloatingEvents(events) {
    if (!events || events.length === 0) {
      return svg``;
    }

    const { PADDING_BASE, PADDING_BASE_DOUBLE } = this.constants();
    const visualHeight = this.yScale(this.maxTotalVolume) / 1.25;
    const visualY = (this.barHeightPadded / 2) - (visualHeight / 2);
    const bottomLineStart = (this.barHeightPadded / 2) + (visualHeight / 2);
    const bottomLineEnd = bottomLineStart + (this.barHeightPadded - bottomLineStart);
    const highlightY = visualY - PADDING_BASE;
    const highlightHeight = visualHeight + PADDING_BASE_DOUBLE;

    return events.map((event) => {
      const scaledDate = this.xScale(event.date);
      const visualX = scaledDate - (this.levelWidth / 2);
      const highlightX = scaledDate - (this.levelWidthPadded / 2);
      const classes = ['item', 'floating-event', ...this.getSelectableClassList(event)];

      return svg`
        <g class="${classes.join(' ')}">
          <rect
            x="${highlightX}"
            y="${highlightY}"
            height="${highlightHeight}"
            width="${this.levelWidthPadded}"
            class="highlight"
          ></rect>
          <rect
            x="${visualX}"
            y="${visualY}"
            height="${visualHeight}"
            width="${this.levelWidth}"
            class="visual"
            rx="4px"
            ry="4px"
          ></rect>
          <line
            class="visual"
            x1="${scaledDate}"
            y1="${visualY}"
            x2="${scaledDate}"
            y2="${0}"
          ></line>
          <line
            class="visual"
            x1="${scaledDate}"
            y1="${bottomLineStart}"
            x2="${scaledDate}"
            y2="${bottomLineEnd}"
          ></line>
        </g>
      `;
    });
  }

  _parseData() {
    // Push Event error data into BinLevel entities
    this.levelsEntities = [...this.levels]
      .map((level) => {
        const event = this.events && this.events.find((event) => event.rawDate === level.rawDate);
        const { sensorCriticalError, lastRunError } = event || {};

        return {
          ...level,
          ...( sensorCriticalError ? { sensorCriticalError } : {} ),
          ...( lastRunError ? { lastRunError } : {} ),
        };
      })
      .sort((a, b) => a.date.diff(b.date));

    // Find Event entities with no matching BinLevel entity
    this.floatingEvents = this.events && this.events.filter((event) => !this.levels.find((level) => level.rawDate === event.rawDate));
  }
}

window.customElements.define('device-monitoring-chart-layer-levels', DeviceMonitoringChartLayerLevels);
